import React, { useState, useEffect } from 'react';
import { FlaskConical, ArrowRight, RefreshCw, AlertTriangle, Flower } from 'lucide-react';
import { synthesizeIkigai } from '../services/geminiService';
import { IkigaiSynthesisResponse } from '../types';
import { Link } from 'react-router-dom';
import { useUser } from '../context/UserContext';

const IkigaiLab: React.FC = () => {
  const [pillars, setPillars] = useState({ love: "", goodAt: "", worldNeeds: "", paidFor: "" });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<IkigaiSynthesisResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { unlockBadge } = useUser();

  useEffect(() => {
    const saved = localStorage.getItem('ikigai_pillars');
    if (saved) {
      setPillars(JSON.parse(saved));
    }
  }, []);

  const hasInputs = pillars.love && pillars.goodAt && pillars.worldNeeds && pillars.paidFor;

  const handleSynthesis = async () => {
    if (!hasInputs) return;
    setLoading(true);
    setError(null);
    try {
      const data = await synthesizeIkigai(pillars);
      setResult(data);
      unlockBadge('ikigai_seeker');
    } catch (err) {
      setError("The lab failed to synthesize. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 pb-20 animate-fadeIn">
       <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-teal-50 text-teal-600 rounded-full mb-4">
          <FlaskConical size={32} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-3">Synthesis Lab</h1>
        <p className="text-nunchi-600 max-w-lg mx-auto">
          Use AI to analyze the intersection of your four pillars and discover concrete paths for your future.
        </p>
      </header>

      {!hasInputs ? (
         <div className="bg-orange-50 border border-orange-200 rounded-xl p-8 text-center">
            <AlertTriangle className="mx-auto text-orange-400 mb-4" size={32} />
            <h3 className="text-lg font-bold text-orange-800 mb-2">Missing Ingredients</h3>
            <p className="text-orange-700 mb-6">You need to fill out all 4 sections of your Ikigai Map before we can synthesize a result.</p>
            <Link to="/ikigai" className="inline-flex items-center gap-2 bg-white text-orange-600 font-bold px-6 py-2 rounded-lg border border-orange-200 hover:bg-orange-100">
               Go to Map <ArrowRight size={16} />
            </Link>
         </div>
      ) : (
        <>
            {!result && !loading && (
                <div className="bg-white border border-nunchi-200 rounded-2xl p-8 text-center shadow-sm">
                    <div className="grid grid-cols-2 gap-4 mb-8 opacity-50 text-sm">
                        <div className="bg-rose-50 p-2 rounded">❤️ {pillars.love.substring(0, 20)}...</div>
                        <div className="bg-blue-50 p-2 rounded">⭐ {pillars.goodAt.substring(0, 20)}...</div>
                        <div className="bg-amber-50 p-2 rounded">🌍 {pillars.worldNeeds.substring(0, 20)}...</div>
                        <div className="bg-emerald-50 p-2 rounded">💰 {pillars.paidFor.substring(0, 20)}...</div>
                    </div>
                    <button 
                        onClick={handleSynthesis}
                        className="w-full sm:w-auto px-8 py-4 bg-teal-600 text-white font-bold rounded-xl shadow-lg hover:bg-teal-700 transition-all flex items-center justify-center gap-2 mx-auto"
                    >
                        <FlaskConical size={20} /> Synthesize My Ikigai
                    </button>
                </div>
            )}

            {loading && (
                <div className="text-center py-20">
                    <div className="inline-block relative">
                         <div className="w-16 h-16 border-4 border-teal-100 border-t-teal-500 rounded-full animate-spin"></div>
                         <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-teal-600">
                            <Flower size={20} />
                         </div>
                    </div>
                    <p className="mt-4 text-teal-800 font-serif italic">Finding the intersection...</p>
                </div>
            )}

            {result && (
                <div className="space-y-8 animate-fadeIn">
                    <div className="bg-teal-900 text-white p-8 rounded-2xl shadow-xl relative overflow-hidden">
                        <div className="absolute top-0 right-0 w-40 h-40 bg-white opacity-5 rounded-full blur-2xl"></div>
                        <h3 className="text-teal-300 uppercase tracking-widest text-xs font-bold mb-2">Deep Insight</h3>
                        <p className="text-xl md:text-2xl font-serif leading-relaxed italic">
                            "{result.insight}"
                        </p>
                    </div>

                    <h3 className="text-xl font-bold text-nunchi-800 pl-2">Suggested Paths</h3>
                    <div className="grid grid-cols-1 gap-6">
                        {result.suggestions.map((item, idx) => (
                            <div key={idx} className="bg-white p-6 rounded-xl border border-nunchi-200 shadow-sm hover:shadow-md transition-shadow">
                                <div className="flex justify-between items-start mb-2">
                                    <h4 className="text-lg font-bold text-teal-700">{item.role}</h4>
                                    <span className="text-xs font-bold bg-teal-50 text-teal-600 px-2 py-1 rounded-full">
                                        {item.matchScore}% Match
                                    </span>
                                </div>
                                <p className="text-nunchi-600 mb-4">{item.description}</p>
                                <div className="bg-nunchi-50 p-4 rounded-lg text-sm text-nunchi-700">
                                    <strong>Why:</strong> {item.why}
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="text-center pt-8">
                        <button 
                            onClick={handleSynthesis} 
                            className="text-nunchi-500 hover:text-nunchi-800 text-sm font-bold flex items-center gap-2 mx-auto"
                        >
                            <RefreshCw size={14} /> Regenerate Analysis
                        </button>
                    </div>
                </div>
            )}
        </>
      )}
    </div>
  );
};

export default IkigaiLab;